import { axiosInstance, HttpErrorResponse } from 'common/infrastructure/api/http-client';
import { VITE_ENV } from '../../../config';
import { Logger } from 'tslog';
import { dispatch, store } from '../../../redux/store';
import {
  addLog,
  resetLogs,
  filterLogsByTimestamp,
  incrementLogCount,
  removeLogs,
} from 'common/infrastructure/log/repository';
import { LogMessage, InitialLogMeta, ModifiedLogMeta } from './types';
import * as _ from 'lodash';
import sizeof from 'object-sizeof';
import TenantRepository from 'common/infrastructure/tenant/repository';
import {
  init as initTreePlotterSharedLogger,
  Logger as TpSharedLogger,
} from '@planitgeo/treeplotter-domain/lib/infrastructure/log/logger';

export enum LoggerLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error',
  FATAL = 'fatal',
}

export interface EvictionPlan {
  logsToKeep: LogMessage[];
  logsToRemove: LogMessage[];
  finalSize: number;
}

const MAX_LOG_CACHE_SIZE = 10 * 1024 * 1024; // 10MB (reduced from 2GB to prevent memory issues)
const SYNC_INTERVAL_MS = 10000;

export const isLoggingRequest = (url: string): boolean => url.startsWith(VITE_ENV.tp2Url);

const priorityMap: Record<string, number> = {
  [LoggerLevel.FATAL]: 5,
  [LoggerLevel.ERROR]: 4,
  [LoggerLevel.WARN]: 3,
  [LoggerLevel.INFO]: 2,
  [LoggerLevel.DEBUG]: 1,
};

export const getLogLevel = (log: LogMessage): string => {
  if ('level' in log && typeof log.level === 'string') {
    return log.level;
  }
  return LoggerLevel.DEBUG;
};

export const getTimestamp = (log: LogMessage): number =>
  typeof log.date === 'number' ? log.date : Date.now();

export function planEviction(
  existingLogs: readonly LogMessage[],
  incomingLog: LogMessage,
  incomingSize: number,
  maxSize: number
): EvictionPlan {
  const currentSize = existingLogs.reduce((sum, log) => sum + sizeof(log), 0);

  if (currentSize + incomingSize <= maxSize) {
    return {
      logsToKeep: [...existingLogs],
      logsToRemove: [],
      finalSize: currentSize + incomingSize,
    };
  }

  const incomingPriority = priorityMap[getLogLevel(incomingLog)] ?? 1;

  const evictionCandidates = [...existingLogs]
    .filter((log) => (priorityMap[getLogLevel(log)] ?? 1) <= incomingPriority)
    .sort((a, b) => {
      const aPriority = priorityMap[getLogLevel(a)] ?? 1;
      const bPriority = priorityMap[getLogLevel(b)] ?? 1;

      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }

      return getTimestamp(a) - getTimestamp(b);
    });

  const requiredSpace = currentSize + incomingSize - maxSize;
  let freedSpace = 0;
  const logsToRemove: LogMessage[] = [];

  for (const log of evictionCandidates) {
    if (freedSpace >= requiredSpace) break;

    logsToRemove.push(log);
    freedSpace += sizeof(log);
  }

  const projectedSize = currentSize - freedSpace;

  const logsToKeep = existingLogs.filter((log) => !logsToRemove.includes(log));

  return {
    logsToKeep,
    logsToRemove,
    finalSize: projectedSize + incomingSize,
  };
}

export function formatLogMessage(
  rawLog: LogMessage,
  meta: InitialLogMeta,
  tenantPid: string
): LogMessage {
  const formattedMeta: ModifiedLogMeta = {
    tenant: tenantPid,
    level: LoggerLevel[meta.logLevelName],
    date: Date.now(),
    tpMobileVersion: window.appVersion,
    browser: meta.browser,
    stack: meta.stack,
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { path, runtime, name, logLevelId, logLevelName, date, ...rest } = meta;

  let formatted = _.merge({}, rawLog, rest, formattedMeta);
  delete formatted._meta;

  ['0', '1'].forEach((key) => {
    if (formatted[key]) {
      if (typeof formatted[key] === 'object') {
        formatted = _.merge(formatted, formatted[key]);
      } else if (typeof formatted[key] === 'string') {
        formatted.message = formatted[key] as string;
      }
      delete formatted[key];
    }
  });

  return formatted;
}

export function generateHash(log: LogMessage): string {
  try {
    // Only include essential fields for deduplication
    const hashContent = {
      message: log.message,
      level: log.level,
      tenant: log.tenant,
      ...(log.stack && { stack: log.stack }),
    };

    const str = JSON.stringify(hashContent);

    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    return hash.toString(36);
  } catch (error) {
    console.error('[Logger] Failed to generate hash', error);
    return `fallback-${Date.now()}-${Math.random()}`;
  }
}

export function findDuplicateIndex(logs: readonly LogMessage[], hash: string): number {
  return logs.findIndex((log) => log.hash === hash);
}

export function calculateCacheState(logs: LogMessage[]): {
  size: number;
} {
  let size = 0;

  for (const log of logs) {
    size += sizeof(log);
  }

  return { size };
}

export class LoggerService {
  private _currentLogSize = 0;
  private _syncIntervalId: NodeJS.Timeout | null = null;

  private _logger = new Logger({
    type: 'hidden',
    overwrite: {
      transportJSON: (logObjWithMeta) => {
        try {
          this.processLog(logObjWithMeta);
        } catch (error) {
          // Use console.error instead of this.log to prevent infinite recursion
          console.error('[Logger] Failed to process logs', error);
        }
      },
    },
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private processLog(logObjWithMeta: any) {
    const tenantPid = TenantRepository.getCurrentTenantPid();
    const formatted = formatLogMessage(logObjWithMeta, logObjWithMeta._meta, tenantPid);
    const hash = generateHash(formatted);
    const currentLogs = this.getLogs();
    const duplicateIndex = findDuplicateIndex(currentLogs, hash);

    if (duplicateIndex >= 0) {
      dispatch(incrementLogCount(hash));
      return;
    }

    const newLog = { ...formatted, hash, count: 1 } as LogMessage;
    const logSize = sizeof(newLog);

    if (logSize > MAX_LOG_CACHE_SIZE) {
      // Use console.warn instead of this.log to prevent recursion
      // This is a serious issue - a single log shouldn't exceed the cache size
      if (VITE_ENV.showLogs) {
        console.warn('[Logger] Dropped log.', {
          reason: 'size too large',
          level: newLog.level,
          size: logSize,
        });
      }
      return;
    }

    const incomingLogPriority = priorityMap[getLogLevel(newLog)] ?? 1;

    const lowestCachePriority = currentLogs.length
      ? Math.min(...currentLogs.map((log) => priorityMap[getLogLevel(log)] ?? 1))
      : 0;

    if (incomingLogPriority < lowestCachePriority) {
      // Silently drop low-priority logs to avoid console spam
      // Only log if it's an ERROR or FATAL being dropped (which shouldn't happen)
      if (VITE_ENV.showLogs && incomingLogPriority >= priorityMap[LoggerLevel.ERROR]) {
        console.warn('[Logger] Dropped log.', {
          reason: 'priority too low',
          level: newLog.level,
          size: logSize,
        });
      }
      return;
    }

    const plan = planEviction(currentLogs, newLog, logSize, MAX_LOG_CACHE_SIZE);

    if (plan.finalSize > MAX_LOG_CACHE_SIZE) {
      // Silently drop - eviction couldn't free enough space
      // Only log if it's an ERROR or FATAL being dropped (which is concerning)
      if (VITE_ENV.showLogs && incomingLogPriority >= priorityMap[LoggerLevel.ERROR]) {
        console.warn('[Logger] Dropped important log.', {
          reason: 'eviction could not free enough space',
          level: newLog.level,
          size: logSize,
          planFinalSize: plan.finalSize,
          maxSize: MAX_LOG_CACHE_SIZE,
        });
      }
      return;
    }

    this.applyEvictionPlan(plan, newLog);
  }

  private applyEvictionPlan(plan: EvictionPlan, newLog: LogMessage): void {
    this._currentLogSize = plan.finalSize;

    if (plan.logsToRemove.length > 0) {
      dispatch(removeLogs(plan.logsToRemove));
    }
    dispatch(addLog(newLog));
  }

  private rebuildCacheState(logs: LogMessage[]): void {
    const { size } = calculateCacheState(logs);
    this._currentLogSize = size;
  }

  init(isAuthenticated: boolean): void {
    if (this._syncIntervalId !== null) {
      return;
    }

    this._syncIntervalId = setInterval(async () => {
      if (navigator.onLine && this.getLogCount() > 0 && isAuthenticated) {
        if (VITE_ENV.isLocalhost) {
          // clear out log data on local
          dispatch(resetLogs());
          this._currentLogSize = 0;
        } else {
          await this.syncLogs();
        }
      }
    }, SYNC_INTERVAL_MS);
  }

  clear(): void {
    if (this._syncIntervalId !== null) {
      clearInterval(this._syncIntervalId);
      this._syncIntervalId = null;
    }
  }

  private syncLogs = async (): Promise<void> => {
    const logs = this.getLogs();

    try {
      const response = await axiosInstance.post(`${VITE_ENV.tp2Url}/v3/logs`, logs, {});
      if (response && (response?.status === 200 || response?.status === 204)) {
        // 200: All logs accepted
        // 204: All logs rejected as too old (>7 days) - safe to clear
        dispatch(resetLogs());
        this._currentLogSize = 0;
      }
    } catch (error) {
      const httpError = error as HttpErrorResponse;

      if (httpError?.status === 413) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const lastAcceptedTimestamp = (httpError.data as any)?.lastAcceptedTimestamp as number;

        dispatch(filterLogsByTimestamp({ lastAcceptedTimestamp, sentLogs: logs }));
        const filteredLogs = this.getLogs();
        this.rebuildCacheState(filteredLogs);
        return;
      }

      // Use console.error instead of this.log to prevent potential recursion during sync errors
      console.error('[Logger] Failed to sync logs', error);
    }
  };

  getLogs(): LogMessage[] {
    const { logging } = store.getState();
    const { logs } = logging;
    return logs;
  }

  getLogCount(): number {
    const logs = this.getLogs();
    return logs.length;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  log(logLevel: LoggerLevel, message: string, jsonObj?: any): void {
    this._logger[logLevel](message, jsonObj);

    if (VITE_ENV.showLogs) {
      const consoleLevel = logLevel === LoggerLevel.FATAL ? LoggerLevel.ERROR : logLevel;
      if (jsonObj) {
        console[consoleLevel](message, jsonObj);
      } else {
        console[consoleLevel](message);
      }
    }
  }
}

const loggerService = new LoggerService();

export default loggerService;

const treePlotterSharedLogger: TpSharedLogger = {
  error(message: string, meta?: unknown) {
    loggerService.log(LoggerLevel.ERROR, message, meta);
  },

  warn(message: string, meta?: unknown) {
    loggerService.log(LoggerLevel.WARN, message, meta);
  },

  info(message: string, meta?: unknown) {
    loggerService.log(LoggerLevel.INFO, message, meta);
  },

  debug(message: string, meta?: unknown) {
    loggerService.log(LoggerLevel.DEBUG, message, meta);
  },

  silly(message: string, meta?: unknown) {
    loggerService.log(LoggerLevel.DEBUG, message, meta);
  },
};

initTreePlotterSharedLogger(treePlotterSharedLogger);
